import { useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useScroll, useTransform } from "framer-motion";
import { fetchYachts } from "../store/slices/yachtsSlice";
import { fetchBrands } from "../store/slices/brandsSlice";
import { Section, Container, SectionEyebrow } from "../components/ui/Section";
import { AnimatedNumber } from "../components/ui/AnimatedNumber";
import { mediaUrl } from "../utils/media";
import {
  StaggerReveal,
  MotionItem,
  fadeUp,
  fadeUpSmall,
  fadeLeft,
  scaleIn,
} from "../components/ui/Motion";
import {
  PageHeader,
  Title,
  HeaderSub,
  MissionGrid,
  MissionImageWrap,
  MissionImageInner,
  MissionContent,
  MissionTitle,
  MissionDivider,
  MissionText,
  ApproachText,
  ApproachGrid,
  ApproachContent,
  ApproachImageWrap,
  NavySection,
  StatsGrid,
  StatCard,
  StatNumber,
  StatLabel,
  ValuesGrid,
  ValueCard,
  ValueIconWrap,
  ValueTitle,
  ValueDesc,
  // TimelineWrap,
  // TimelineItem,
  // TimelineDot,
  // TimelineContent,
  // TimelineYear,
  // TimelineTitle,
  // TimelineDesc,
  // TeamGrid,
  // TeamCard,
  // TeamPhoto,
  // TeamInitials,
  // TeamInfo,
  // TeamName,
  // TeamRole,
  CtaWrap,
  CtaSection,
  CtaTitle,
  CtaText,
  CtaButton,
  SectionHeader,
  SectionTitle,
} from "./styles/AboutPage.styles";

// SVG icons for values
const DiamondIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 3h12l4 6-10 13L2 9z" />
    <path d="M2 9h20" />
    <path d="M10 3l-4 6 6 13 6-13-4-6" />
  </svg>
);

const ShieldIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const CompassIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
  </svg>
);

// const timelineData = [
//   {
//     year: "2014",
//     title: "The Beginning",
//     desc: "Founded with a simple vision: to make premium yachting accessible to discerning clients across the Adriatic.",
//   },
//   {
//     year: "2016",
//     title: "First Major Partnership",
//     desc: "Secured exclusive brokerage deals with leading European shipyards, expanding our portfolio to over 30 vessels.",
//   },
//   {
//     year: "2019",
//     title: "Regional Expansion",
//     desc: "Opened offices in Montenegro and Croatia, bringing our expertise closer to the Mediterranean's finest marinas.",
//   },
//   {
//     year: "2022",
//     title: "Digital Transformation",
//     desc: "Launched our online platform, enabling clients worldwide to explore and inquire about yachts from anywhere.",
//   },
//   {
//     year: "2024",
//     title: "50+ Yachts & Growing",
//     desc: "Surpassed 50 listed yachts and 200 satisfied clients, cementing our position as a trusted name in yacht brokerage.",
//   },
// ];

function AboutPage() {
  const dispatch = useDispatch();
  const { items: yachtItems, count: yachtCount } = useSelector(
    (state) => state.yachts,
  );
  const { items: brands } = useSelector((state) => state.brands);

  useEffect(() => {
    dispatch(fetchYachts());
    dispatch(fetchBrands());
  }, [dispatch]);

  // Real portfolio figures pulled from the API; the other two stats are
  // editorial claims kept until verified numbers are supplied.
  const yachtsListed = yachtCount || yachtItems.length || 0;
  const premiumBrands = brands.length || 0;
  const stats = [
    { value: yachtsListed, suffix: "", label: "Yachts Listed" },
    { value: premiumBrands, suffix: "", label: "Premium Brands" },
    { value: 10, suffix: "+", label: "Years Experience" },
    { value: 200, suffix: "+", label: "Happy Clients" },
  ];

  const missionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: missionRef,
    offset: ["start end", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <>
      {/* Hero - unchanged */}
      <PageHeader>
        <Container>
          <StaggerReveal>
            <MotionItem $variants={fadeUpSmall}>
              <SectionEyebrow>About Us</SectionEyebrow>
            </MotionItem>
            <MotionItem $variants={fadeUp}>
              <Title>
                Driven by <em>Passion</em>
              </Title>
            </MotionItem>
            <MotionItem $variants={fadeUpSmall}>
              <HeaderSub>
                LUMA Yachting is a premium yacht brokerage connecting clients
                with the world's finest vessels.
              </HeaderSub>
            </MotionItem>
          </StaggerReveal>
        </Container>
      </PageHeader>

      {/* Mission */}
      <Section>
        <Container>
          <MissionGrid ref={missionRef}>
            <MissionImageWrap $variants={fadeLeft}>
              <MissionImageInner style={{ y: imageY }}>
                <img
                  src={mediaUrl("/images/princess/X95/large/hero.webp")}
                  alt="Luxury Princess motor yacht cruising across the open sea"
                  loading="lazy"
                />
              </MissionImageInner>
            </MissionImageWrap>
            <MissionContent slow>
              <MotionItem $variants={fadeUpSmall}>
                <SectionEyebrow>Our Mission</SectionEyebrow>
              </MotionItem>
              <MotionItem $variants={fadeUp}>
                <MissionTitle>Connecting People with the Sea</MissionTitle>
              </MotionItem>
              <MotionItem $variants={fadeUpSmall}>
                <MissionDivider />
              </MotionItem>
              <MotionItem $variants={fadeUpSmall}>
                <MissionText>
                  At Luma Yachting, we specialize in connecting discerning
                  clients with the finest yachts available on the global market.
                  Our offering includes premium, world-renowned brands as well
                  as a handpicked collection of brokerage yachts that meet the
                  highest standards of quality and performance.
                </MissionText>
              </MotionItem>
              <MotionItem $variants={fadeUpSmall}>
                <MissionText>
                  Backed by industry expertise and a personalized approach, we
                  provide strategic guidance, market insight, and full-service
                  support, ensuring every decision is informed, confident, and
                  aligned with your vision of life at sea.
                </MissionText>
              </MotionItem>
            </MissionContent>
          </MissionGrid>
        </Container>
      </Section>

      {/* Approach */}
      <Section $bg="offWhite">
        <Container>
          <ApproachGrid>
            <ApproachContent slow>
              <MotionItem $variants={fadeUpSmall}>
                <SectionEyebrow>Our Approach</SectionEyebrow>
              </MotionItem>
              <MotionItem $variants={fadeUp}>
                <SectionTitle>
                  A New Standard in <em>Luxury Yachting</em>
                </SectionTitle>
              </MotionItem>
              <MotionItem $variants={fadeUpSmall}>
                <ApproachText>
                  Luma Yachting goes beyond brokerage. As authorized
                  representatives of Princess Yachts, we offer the full Princess
                  range alongside a carefully curated portfolio of world-class
                  new builds and an exclusive selection of pre-owned brokerage
                  yachts.
                </ApproachText>
              </MotionItem>
              <MotionItem $variants={fadeUpSmall}>
                <ApproachText>
                  With a deep understanding of the market and a commitment to
                  excellence, we guide every client through the acquisition
                  process, whether you are seeking a brand-new vessel or a
                  distinguished pre-owned yacht, ensuring a seamless, discreet,
                  and elevated experience tailored to your lifestyle.
                </ApproachText>
              </MotionItem>
            </ApproachContent>
            <ApproachImageWrap $variants={fadeLeft}>
              <img
                src={mediaUrl(
                  "/images/princess/Y95/interior/large/interior-02_y95-saloon-4-rt.webp",
                )}
                alt="Elegant main saloon of a Princess yacht opening onto the sea"
                loading="lazy"
              />
            </ApproachImageWrap>
          </ApproachGrid>
        </Container>
      </Section>

      {/* Stats */}
      <NavySection>
        <Container>
          <StatsGrid>
            {stats.map((s) => (
              <StatCard key={s.label} $variants={scaleIn}>
                <StatNumber>
                  <AnimatedNumber value={s.value} />
                  {s.suffix}
                </StatNumber>
                <StatLabel>{s.label}</StatLabel>
              </StatCard>
            ))}
          </StatsGrid>
        </Container>
      </NavySection>

      {/* Values */}
      <Section>
        <Container>
          <SectionHeader>
            <MotionItem $variants={fadeUpSmall}>
              <SectionEyebrow>Our Values</SectionEyebrow>
            </MotionItem>
            <MotionItem $variants={fadeUp}>
              <SectionTitle>What We Stand For</SectionTitle>
            </MotionItem>
          </SectionHeader>

          <ValuesGrid slow>
            <ValueCard $variants={scaleIn}>
              <ValueIconWrap>
                <DiamondIcon />
              </ValueIconWrap>
              <ValueTitle>Excellence</ValueTitle>
              <ValueDesc>
                We never compromise on quality. Every yacht in our portfolio is
                personally inspected and vetted by our expert team.
              </ValueDesc>
            </ValueCard>
            <ValueCard $variants={scaleIn}>
              <ValueIconWrap>
                <ShieldIcon />
              </ValueIconWrap>
              <ValueTitle>Trust</ValueTitle>
              <ValueDesc>
                Transparency and integrity are at the heart of everything we do.
                Our clients trust us to guide them through one of life's most
                significant investments.
              </ValueDesc>
            </ValueCard>
            <ValueCard $variants={scaleIn}>
              <ValueIconWrap>
                <CompassIcon />
              </ValueIconWrap>
              <ValueTitle>Passion</ValueTitle>
              <ValueDesc>
                We're not just brokers. We're yacht enthusiasts, and our passion
                for the sea drives us to deliver exceptional experiences to
                every client.
              </ValueDesc>
            </ValueCard>
          </ValuesGrid>
        </Container>
      </Section>

      {/* Timeline */}
      {/* <Section $bg="offWhite">
        <Container>
          <SectionHeader>
            <MotionItem $variants={fadeUpSmall}>
              <SectionEyebrow>Our Journey</SectionEyebrow>
            </MotionItem>
            <MotionItem $variants={fadeUp}>
              <SectionTitle>A Decade of Excellence</SectionTitle>
            </MotionItem>
          </SectionHeader>

          <TimelineWrap>
            {timelineData.map((item) => (
              <TimelineItem key={item.year} $variants={fadeUp}>
                <TimelineDot />
                <TimelineContent>
                  <TimelineYear>{item.year}</TimelineYear>
                  <TimelineTitle>{item.title}</TimelineTitle>
                  <TimelineDesc>{item.desc}</TimelineDesc>
                </TimelineContent>
              </TimelineItem>
            ))}
          </TimelineWrap>
        </Container>
      </Section> */}

      {/* Team */}
      {/* <NavySection>
        <Container>
          <SectionHeader>
            <MotionItem $variants={fadeUpSmall}>
              <SectionEyebrow>Our Team</SectionEyebrow>
            </MotionItem>
            <MotionItem $variants={fadeUp}>
              <SectionTitle>Meet the Crew</SectionTitle>
            </MotionItem>
          </SectionHeader>

          <TeamGrid slow>
            <TeamCard $variants={scaleIn}>
              <TeamPhoto>
                <TeamInitials>SU</TeamInitials>
              </TeamPhoto>
              <TeamInfo>
                <TeamName>Srđan Uzunović</TeamName>
                <TeamRole>Full-Stack Developer</TeamRole>
              </TeamInfo>
            </TeamCard>
            <TeamCard $variants={scaleIn}>
              <TeamPhoto>
                <TeamInitials>AG</TeamInitials>
              </TeamPhoto>
              <TeamInfo>
                <TeamName>Aleksandar Gajinov</TeamName>
                <TeamRole>Brand & Visual Designer</TeamRole>
              </TeamInfo>
            </TeamCard>
          </TeamGrid>
        </Container>
      </NavySection> */}

      {/* CTA */}
      <Section style={{ paddingTop: 0, paddingBottom: 0 }}>
        <Container>
          <CtaWrap>
            <StaggerReveal>
              <MotionItem $variants={fadeUp}>
                <CtaSection>
                  <CtaTitle>
                    Ready to Find Your <em>Dream Yacht</em>?
                  </CtaTitle>
                  <CtaText>
                    Whether you're a first-time buyer or a seasoned yachtsman,
                    our team is here to help you navigate every step of the
                    journey.
                  </CtaText>
                  <CtaButton to="/contact">Request a Quote</CtaButton>
                </CtaSection>
              </MotionItem>
            </StaggerReveal>
          </CtaWrap>
        </Container>
      </Section>
    </>
  );
}

export default AboutPage;
